/**
 * ImportWizard — public prop and state types.
 * @layer widget
 * @package @plerity/dashboard-shared
 */

import type {
  ColumnMapping,
  CsvCommitOptions,
  CsvCommitResult,
  CsvImportTarget,
  CsvPreview,
  CsvRowIssue,
  FormulaPolicy,
  HeaderRename,
  ValidatedRow,
  ValidateResult,
} from "@plerity/csv";

// ---------------------------------------------------------------------------
// Upsert (optional, default off)
// ---------------------------------------------------------------------------

/**
 * What to do with a row that matches an existing record (or an earlier row in the same file).
 * `update` = write the row's values onto the matched record; `create` = add a new record anyway;
 * `skip` = leave the row out.
 */
export type ImportRowDecision = "create" | "update" | "skip";

/** One row the target found a duplicate for. Keyed by `ValidatedRow.index` (spreadsheet row; header = 1). */
export interface ImportDuplicateMatch {
  /** `ValidatedRow.index` of the incoming row. */
  row: number;
  /**
   * `existing`: the row matches a record already saved (update is possible).
   * `file`: the row repeats an earlier row in the same file (no record to update yet).
   */
  source: "existing" | "file";
  /** Id of the matched record, for `existing` matches. Shown as `#<id>` when no label is given. */
  matchId?: string | number | null;
  /** Human label for the matched record ("Acme Corp", "Row 4"). */
  matchLabel?: string | null;
  /** Field key that matched (one of `dedupeKeys`), or `null` when the target does not say. */
  field?: string | null;
  /** The value that matched ("ada@example.com"). */
  value?: string | null;
  /** Decisions this row may take. Default: `existing` → update/create/skip, `file` → create/skip. */
  allowed?: ReadonlyArray<ImportRowDecision>;
  /** Initial decision for this row. Falls back to `ImportUpsertCapability.defaultDecision`. */
  defaultDecision?: ImportRowDecision;
}

/** `CsvCommitOptions` plus the user's per-row decisions (only sent when the target opts into upsert). */
export interface ImportCommitOptions extends CsvCommitOptions {
  /**
   * Decision for every duplicate row in THIS batch, keyed by `ValidatedRow.index`. Present only on the real
   * commit (`dryRun: false`) of a target with `upsert`; rows without an entry had no duplicate match.
   * Rows decided `skip` are still sent in the batch so all-or-nothing / token-based targets see the full set.
   */
  decisions?: Readonly<Record<number, ImportRowDecision>>;
}

/** `CsvCommitResult` plus duplicate matches a dry run may report. */
export interface ImportCommitResult extends CsvCommitResult {
  /** Duplicate matches found while dry-running this batch. Read only when the target has `upsert`. */
  duplicates?: ReadonlyArray<ImportDuplicateMatch>;
}

/**
 * Opt-in upsert capability. When a target carries this, the review step lists every row that matches an
 * existing record (or an earlier row in the file) and lets the user pick create / update / skip per row, with
 * a bulk "apply to all" control. The decisions travel to `commit()` in `options.decisions`.
 *
 * Duplicates are collected from the dry run's `ImportCommitResult.duplicates` (one server round-trip, the
 * usual shape: a preview endpoint that returns per-row matches) and/or from `findDuplicates` (a separate
 * preview hook, for targets whose dry run cannot report them). Both may be used; a later match for the same
 * row replaces an earlier one.
 */
export interface ImportUpsertCapability<T> {
  /** Field keys used to detect duplicates, for the review copy ("Matched on Email or Phone"). */
  dedupeKeys: readonly string[];
  /** Decision a matched row starts with when the match names none (default `skip`: never overwrite silently). */
  defaultDecision?: ImportRowDecision;
  /** Optional preview hook, called once after a successful dry run with every row the dry run did not refuse. */
  findDuplicates?: (
    rows: ReadonlyArray<ValidatedRow<T>>,
    options: { signal: AbortSignal }
  ) => Promise<ReadonlyArray<ImportDuplicateMatch>>;
  /** Override the option labels in the per-row select. */
  decisionLabels?: Partial<Record<ImportRowDecision, string>>;
}

/**
 * The target the wizard accepts: a plain `CsvImportTarget` (unchanged behaviour) or one that also opts into
 * upsert. Any `CsvImportTarget<T>` is assignable to this type.
 */
export interface ImportWizardTarget<T> extends Omit<CsvImportTarget<T>, "commit"> {
  commit(rows: ReadonlyArray<ValidatedRow<T>>, options: ImportCommitOptions): Promise<ImportCommitResult>;
  /** Opt into per-row create / update / skip for duplicates. Default off. */
  upsert?: ImportUpsertCapability<T>;
}

export type ImportStep = "upload" | "map" | "validate" | "review" | "done";

export const IMPORT_STEPS: ReadonlyArray<{ id: ImportStep; label: string }> = [
  { id: "upload", label: "Upload" },
  { id: "map", label: "Map columns" },
  { id: "validate", label: "Check rows" },
  { id: "review", label: "Review" },
  { id: "done", label: "Done" },
];

/** A row the target refused during the dry run or the real commit. */
export interface ServerRowIssue {
  row: number;
  field?: string | null;
  message: string;
}

export interface BatchTotals {
  created: number;
  updated: number;
  skipped: number;
  errors: ServerRowIssue[];
}

export interface ImportRunSummary {
  fileName: string;
  created: number;
  updated: number;
  skipped: number;
  /** Rows left out because they failed validation here. */
  invalidRows: number;
  /** Rows left out because the target rejected them. */
  rejectedRows: number;
}

export interface ImportWizardProps<T> {
  /** What is being imported and how it is persisted. Add `upsert` to get per-row duplicate decisions. */
  target: ImportWizardTarget<T>;
  /** Heading text. Default `Import <entityName> from CSV`. */
  title?: string;
  /**
   * Rows sent per `commit()` call (default 500). `Number.POSITIVE_INFINITY` sends everything in one call,
   * which gives the target all-or-nothing semantics.
   */
  batchSize?: number;
  /** Largest number of data rows accepted (default 20,000). More is an error, never a silent cut. */
  maxRows?: number;
  /** Largest file accepted, in bytes (default 100 MiB). */
  maxFileBytes?: number;
  /**
   * Let the user import the valid rows while skipping the ones with problems (default `true`). The user
   * must tick an explicit box that names how many rows will be left out. `false` blocks the import until
   * the file is fixed.
   */
  allowSkipInvalid?: boolean;
  /** What to do with cells that start with `= + - @` (default `flag`: keep and warn). */
  formulaPolicy?: FormulaPolicy;
  /** Called once after a successful commit. */
  onComplete?: (summary: ImportRunSummary) => void;
  /** Shows a Cancel button on the first step when provided. */
  onCancel?: () => void;
  /** Value of `data-help-context` on the wizard root (default `csv-import-wizard`). */
  helpContext?: string;
  className?: string;
}

export interface UseCsvImportOptions {
  batchSize?: number;
  maxRows?: number;
  maxFileBytes?: number;
  allowSkipInvalid?: boolean;
  formulaPolicy?: FormulaPolicy;
  onComplete?: (summary: ImportRunSummary) => void;
}

export type PhaseStatus = "idle" | "running" | "done" | "error";

export interface ImportWizardState<T> {
  step: ImportStep;
  file: File | null;
  upload: { status: "idle" | "reading" | "error"; message: string | null };
  preview: (Pick<CsvPreview["meta"], "delimiter" | "encoding" | "hadBom"> & { rows: string[][] }) | null;
  hasHeader: boolean;
  headers: string[];
  renamed: HeaderRename[];
  mapping: ColumnMapping;
  validate: {
    status: PhaseStatus;
    /** 0..1 */
    progress: number;
    message: string | null;
    result: ValidateResult<T> | null;
  };
  /** The user ticked "skip the rows with problems". */
  skipInvalid: boolean;
  dryRun: { status: PhaseStatus; done: number; total: number; message: string | null; totals: BatchTotals | null };
  commit: {
    status: PhaseStatus;
    done: number;
    total: number;
    message: string | null;
    totals: BatchTotals;
    /** Index of the next batch to send; lets "Retry" resume instead of re-sending finished batches. */
    nextBatch: number;
  };
  /** Duplicate matches + the user's decisions. Stays empty unless the target has `upsert`. */
  upsert: {
    duplicates: ImportDuplicateMatch[];
    /** Keyed by `ValidatedRow.index`. */
    decisions: Record<number, ImportRowDecision>;
  };
}

export type { CsvRowIssue };
